<template>
  <div
    ref="chartContainer"
    class="chart-el"
    @mouseout="tooltipHidden = true"
    @mouseover="tooltipHidden = false"
  >
    <canvas
      :style="{ width: `${chartWidth / 2}px`, height: `${716 / 2}px` }"
      :width="chartWidth"
      height="716"
      ref="chart-ctx3"
      class="helper"
    />
    <div class="chart-name chart-name_1">{{chartType}}</div>
    <div class="chart-name chart-name_2">Volume 24h</div>
    <div class="chart-container1">
      <canvas @mousemove="onMouseMove" ref="chart-ctx" id="chart" class="chart" />
    </div>
    <div class="chart-container2">
      <canvas @mousemove="onMouseMove" ref="chart-ctx2" id="chart2" class="chart2" />
    </div>
    <tooltip
      :position="tooltipPosition"
      :date="tooltipDate"
      :time="tooltipTime"
      :chartType="chartType"
      :marketcap="tooltipCap"
      :volume24h="tooltipVolume24h"
      v-if="!tooltipHidden && tooltipInitialized"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import Chart from 'chart.js';
import { format } from 'date-fns';
import differenceInMonths from 'date-fns/difference_in_months';
import differenceInCalendarDays from 'date-fns/difference_in_calendar_days';
import minBy from 'lodash/minBy';
import maxBy from 'lodash/maxBy';
import round from 'lodash/round';
import { optionsBlack, optionsRed } from 'src/utils/chartSettings';
import { formatMoney } from 'src/utils/helpers';
import Tooltip from './Tooltip';

const chartPaddingLeft = 19;// if you want to change it, change also CMD+F chartPaddingLeft

export default {
  components: {
    Tooltip,
  },
  props: {
    chartData1: {
      type: Array,
      required: true,
    },
    chartData2: {
      type: Array,
      required: true,
    },
    chartType: {
      type: String,
      required: true,
    },
  },
  data: () => ({
    prevActivePoints: [],
    chart1: null,
    chart2: null,
    tooltipPosition: {
      left: 0,
      top: 0,
    },
    chartWidth: 0,
    tooltipHidden: true,
    tooltipDate: '',
    tooltipTime: '',
    tooltipCap: 0,
    tooltipVolume24h: 0,
    tooltipInitialized: false,
  }),
  mounted() {
    this.initChart();
    window.addEventListener('resize', this.scaleHelperCanvas);
  },
  beforeDestory() {
    window.removeEventListener('resize', this.scaleHelperCanvas);
  },
  methods: {
    formatLabel(value, additionalHandler) {
      const billion = 1000000000;
      const million = 1000000;
      if (value > billion) {
        return `${additionalHandler(round(value / billion))} B`;
      }
      if (value > (billion / 10)) {
        return `${additionalHandler(round(value / billion, 2))} B`;
      }
      if (value > million) {
        return `${additionalHandler(round(value / million))} M`;
      }
      if (value > (million / 10)) {
        return `${additionalHandler(round(value / million, 2))} M`;
      }
      if (value > 100) {
        return additionalHandler(round(value));
      }
      return additionalHandler(round(value, 2));
    },
    initChart() {
      optionsRed.scales.yAxes[0].ticks = {
        ...optionsRed.scales.yAxes[0].ticks,
        min: this.chart1MinY,
        max: this.chart1MaxY,
        stepSize: this.chart1Step,
        callback: l => this.formatLabel(l, v => formatMoney(v, this.currencyCode)),
      };
      optionsRed.scales.xAxes[0].time.unit = this.dateUnit;
      this.chart1 = new Chart('chart', {
        type: 'line',
        data: {
          datasets: [{
            label: 'My First dataset',
            lineTension: 0,
            backgroundColor: 'white',
            borderColor: '#D0000A',
            borderWidth: 1,
            data: this.chartData1,
            fill: false,
            yAxisID: 'A',
          }],
        },
        options: optionsRed,
      });

      optionsBlack.scales.yAxes[0].ticks = {
        ...optionsBlack.scales.yAxes[0].ticks,
        min: this.chart2MinY,
        max: this.chart2MaxY,
        stepSize: this.chart2Step,
        callback: l => this.formatLabel(l, v => v),
      };
      this.chart2 = new Chart('chart2', {
        type: 'line',
        data: {
          datasets: [{
            label: 'My First dataset',
            lineTension: 0,
            backgroundColor: 'white',
            borderColor: 'black',
            borderWidth: 1,
            data: this.chartData2,
            fill: false,
          }],
        },
        options: optionsBlack,
      });
      this.scaleHelperCanvas();
    },
    /**
     * Need for supporting of retina screens
     */
    scaleHelperCanvas() {
      this.chartWidth = (this.chart1.width - 118) * 2;
      const ctx3 = this.$refs['chart-ctx3'].getContext('2d');
      ctx3.scale(2, 2);
    },
    onMouseMove(e) {
      this.updateActivePoint(this.chart1, 0, e);
      this.updateActivePoint(this.chart2, 1, e);
      this.drawCrosshairs(e, this.$refs['chart-ctx3']);
    },
    updateActivePoint(chart, prevActivePointIndex, e) {
      const points = chart.getElementsAtXAxis(e);
      if (this.prevActivePoints[prevActivePointIndex]) {
        this.prevActivePoints[prevActivePointIndex].custom = null;
      }
      this.prevActivePoints[prevActivePointIndex] = points[0];
      points[0].custom = {
        radius: 6,
        borderWidth: 2,
      };
      chart.update();
    },
    drawCrosshairs(e, canvas) {
      const points = this.chart1.getElementsAtXAxis(e);
      // eslint-disable-next-line
      const xWithOffset = (points[0]._model.x - chartPaddingLeft);
      const x = xWithOffset * 2;
      this.moveTooltip(xWithOffset, points[0]._model.y, points[0]._index); // eslint-disable-line
      const ctx = canvas.getContext('2d');
      ctx.lineWidth = 1;
      ctx.strokeStyle = '#E5E5E5';
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.beginPath();
      // draw first chart crosshair
      ctx.moveTo(x, 0);
      ctx.lineTo(x, 442);
      // draw second chart crosshair
      ctx.moveTo(x, 551);
      ctx.lineTo(x, canvas.height);
      ctx.stroke();
      ctx.closePath();
    },
    moveTooltip(x, y, index) {
      const canvasWidth = this.$refs['chart-ctx'].width / 2;
      const tooltipWidth = 261;// before change do CMD+F tooltipWidth
      const tooltipTopOffset = 5;
      if (window.innerWidth <= 600) {
        this.tooltipPosition.left = '-99px';
        this.tooltipPosition.right = '0';
        this.tooltipPosition.margin = 'auto';
      } else {
        const cursorOnLeftPartOfCanvas = canvasWidth / 2 > x;
        this.tooltipPosition.left = `${cursorOnLeftPartOfCanvas ? x : x - tooltipWidth}px`;
        this.tooltipPosition.right = null;
        this.tooltipPosition.margin = null;
      }
      this.tooltipPosition.top = `${y + tooltipTopOffset}px`;
      const date = this.chartData1[index].x;
      const val1 = this.chartData1[index].y;
      const val2 = this.chartData2[index].y;
      this.tooltipDate = format(date, 'dddd, MMM D, YYYY');
      this.tooltipTime = format(date, 'HH:mm:ss, UTC Z');
      this.tooltipCap = formatMoney(val1, this.currencyCode);
      this.tooltipVolume24h = formatMoney(val2, this.currencyCode);
      if (!this.tooltipInitialized) {
        this.tooltipInitialized = true;
      }
    },
    calculateChartMax(arrMaxY, arrMinY) {
      return arrMaxY + this.calculateChartPadding(arrMaxY, arrMinY);
    },
    calculateChartMin(arrMaxY, arrMinY) {
      const min = arrMinY - this.calculateChartPadding(arrMaxY, arrMinY);
      return min < 0 ? 0 : min;
    },
    calculateChartPadding(arrMaxY, arrMinY) {
      return (arrMaxY - arrMinY) * 0.2;
    },
  },
  computed: {
    ...mapGetters('userData', ['currencyCode']),
    dateUnit() {
      const maxDate = maxBy(this.chartData1, d => d.x).x;
      const minDate = minBy(this.chartData1, d => d.x).x;
      if (differenceInMonths(maxDate, minDate) > 3) {
        return 'month';
      }
      if (differenceInCalendarDays(maxDate, minDate) > 2) {
        return 'day';
      }
      return 'hour';
    },
    chartData1MaxY() {
      return maxBy(this.chartData1, v => v.y).y;
    },
    chartData1MinY() {
      return minBy(this.chartData1, v => v.y).y;
    },
    chartData2MaxY() {
      return maxBy(this.chartData2, v => v.y).y;
    },
    chartData2MinY() {
      return minBy(this.chartData2, v => v.y).y;
    },
    chart1MaxY() {
      return this.calculateChartMax(this.chartData1MaxY, this.chartData1MinY);
    },
    chart1MinY() {
      return this.calculateChartMin(this.chartData1MaxY, this.chartData1MinY);
    },
    chart1Step() {
      return (this.chart1MaxY - this.chart1MinY) / 5;
    },
    chart2MaxY() {
      return this.calculateChartMax(this.chartData2MaxY, this.chartData2MinY);
    },
    chart2MinY() {
      return this.calculateChartMin(this.chartData2MaxY, this.chartData2MinY);
    },
    chart2Step() {
      return (this.chart2MaxY - this.chart2MinY) / 2;
    },
  },
};
</script>


<style lang="scss" scoped>
.helper {
  left: 0px;
  position: absolute;
  top: 6px;
  pointer-events: none;
}
.chart-el {
  position: relative;
}

.chart-container1 {
  height: 253px;
  position: relative;
}

.chart-container2 {
  height: 132px;
  margin-top: 23px;
  position: relative;
}

.chart-container1,
.chart-container2 {
  margin-left: -19px; // if you want to change it, change also CMD+F chartPaddingLeft
}

.chart-name {
  position: absolute;
  left: 10px;

  @include font(12px, $grey-color2, 500);
  &_1 {
    top: 205px;
  }
  &_2 {
    top: 343px;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/pages/currency/chartBlock/ChartEl.vue